import { Button } from '@app/components/various/Button';
import { cn } from '@app/utils/cn';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Features } from 'typings';

import { Carousel } from './Carousel';

interface Props {
  onboarding: Features.Onboarding;
  hideOnboarding: (id: string) => void;
}

export const Onboarding = ({ onboarding, hideOnboarding }: Props) => {
  const { pages } = onboarding;

  const [currentPageIndex, setCurrentPageIndex] = React.useState(0);
  const { t } = useTranslation(['onboarding']);
  const lastPageIndex = pages.length - 1;

  const isFirstPage = currentPageIndex === 0;
  const isLastPage = currentPageIndex === lastPageIndex;

  const handleCloseTutorial = React.useCallback(() => {
    hideOnboarding(onboarding.id);
  }, [hideOnboarding, onboarding.id]);

  const handleSetPage = (pageIndex: number) => setCurrentPageIndex(pageIndex);

  const handleNextPage = React.useCallback(() => {
    const nextPage = currentPageIndex + 1;

    if (nextPage > lastPageIndex) {
      return;
    }

    handleSetPage(nextPage);
  }, [currentPageIndex, handleSetPage, lastPageIndex]);

  const handlePreviousPage = React.useCallback(() => {
    const prevPage = currentPageIndex - 1;

    if (prevPage < 0) {
      return;
    }

    handleSetPage(prevPage);
  }, [currentPageIndex, handleSetPage]);

  const nextButtonNameLookup = [
    { condition: isFirstPage, name: t('onboarding:find_out_more') },
    { condition: isLastPage, name: t('onboarding:finish') },
    { condition: !isFirstPage && !isLastPage, name: t('onboarding:next') },
  ];

  const nextPageButtonName = nextButtonNameLookup.find(({ condition }) => condition)?.name;

  const nextPageButtonInteraction = isLastPage ? handleCloseTutorial : handleNextPage;

  return (
    <aside className="fixed bottom-0 right-0 z-cmsOnboarding m-4 max-w-96 overflow-hidden rounded-small shadow-1">
      <h2 className="sr-only">{t(onboarding.nameI18nKey)}</h2>

      <Carousel currentStep={currentPageIndex}>
        {pages.map(page => (
          <img key={page.imageUrl} className="w-full object-cover" src={page.imageUrl} alt="" />
        ))}
      </Carousel>

      <div className="bg-white">
        <ul className="list-style-none flex justify-center gap-1 py-3">
          {pages.map((_, idx) => {
            return (
              <li key={idx}>
                <button onClick={() => handleSetPage(idx)} className="flex size-6 items-center justify-center">
                  <div
                    className={cn('size-2 rounded-full bg-neutral-60 transition-colors duration-700', {
                      'bg-black': idx === currentPageIndex,
                    })}
                  />
                  <span className="sr-only">{t('onboarding:step', { id: idx })}</span>
                </button>
              </li>
            );
          })}
        </ul>

        <Carousel currentStep={currentPageIndex}>
          {pages.map(page => (
            <div key={page.titleI18nKey} className="px-6" aria-live="polite">
              <h3 className="py-1 heading-3">{t(page.titleI18nKey)}</h3>
              <p className="py-2 body-5">{t(page.descriptionI18nKey)}</p>
            </div>
          ))}
        </Carousel>

        <div className="flex items-center justify-between gap-3 px-6 py-4">
          <Button onClick={handleCloseTutorial} dataTestId="skipIntro" size="medium" color="secondary" variant="text">
            {t('onboarding:skip_intro')}
          </Button>
          <div className="flex gap-2">
            {!isFirstPage && (
              <Button onClick={handlePreviousPage} color="secondary" variant="bordered" dataTestId="nextIntroStep" size="medium">
                {t('onboarding:previous')}
              </Button>
            )}
            <Button onClick={nextPageButtonInteraction} dataTestId="nextIntroStep" size="medium">
              {nextPageButtonName}
            </Button>
          </div>
        </div>
      </div>
    </aside>
  );
};
